import React, { useState } from 'react';
import { BorderColor, DragIndicator, DeleteOutlined, VisibilityOff, Visibility, ExpandMore } from '@material-ui/icons';
import { Box, Button, Theme, Typography, List, ListItem } from '@material-ui/core';
import { BooleanParam, StringParam, useQueryParam } from 'use-query-params';
import { makeStyles, createStyles } from '@material-ui/styles';
import { useHistory, useLocation } from 'react-router';
import { Skeleton } from '@material-ui/lab';
import { v4 as uuidv4 } from 'uuid';
import { DragDropContext, Droppable, Draggable, DraggableListItemHandle } from '@campfire/draggable-list';
import { TabletButton } from '@campfire/tablet-button';
import { CollapsibleSideBar } from '../../../../common/CollapsibleSideBar';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { Task, DraggableTask } from '../Task/Task';
import { TaskDetails } from '../TaskDetails/TaskDetails';
import { OnboardingOptionTask } from '../OnboardingOptionTask/OnboardingOptionTask';
import { TaskListGetCakes, TaskListGetCakes_vm_cakes as CakeType } from './__generated__/TaskListGetCakes';
import { GET_CAKE, GET_CAKES } from './cake-model.gql';
import { GET_TASK_STAGES } from './task-stages.gql';
import { GET_ONBOARDING_OPTION_STAGES } from '../../../../common/onboarding-option-task/onboarding-option-stages-model.gql';
import {
  GET_APPLICATION_ENROL_ACTIVITIES,
  GET_APPLICATION_APPLY_ROLES,
  GET_APPLICATION_AVAILABILITY_SIMPLE,
  GET_APPLICATION_AVAILABILITY_COMPLEX,
} from '../../admin-console/admin-console-content-pages/admin-console-onboarding-applications/admin-console-applicant-screening.gql';
import { GetTaskStages } from './__generated__/GetTaskStages';
import { GetOnboardingOptionStages } from '../../../../common/onboarding-option-task/__generated__/GetOnboardingOptionStages';
import { GetApplicationEnrolActivities } from '../../admin-console/admin-console-content-pages/admin-console-onboarding-applications/__generated__/GetApplicationEnrolActivities';
import { GetApplicationApplyRoles } from '../../admin-console/admin-console-content-pages/admin-console-onboarding-applications/__generated__/GetApplicationApplyRoles';
import { GetApplicationAvailabilitySimple } from '../../admin-console/admin-console-content-pages/admin-console-onboarding-applications/__generated__/GetApplicationAvailabilitySimple';
import { GetApplicationAvailabilityComplex } from '../../admin-console/admin-console-content-pages/admin-console-onboarding-applications/__generated__/GetApplicationAvailabilityComplex';
import { useDeepEffect } from '../../../../hooks/useDeepEffect';
import { useCampfireLazyQuery } from '../../../../global/network/useCampfireLazyQuery';
import {
  TaskListGetCake,
  TaskListGetCakeVariables,
  TaskListGetCake_vm_cake_allTasks as CakeTaskType,
} from './__generated__/TaskListGetCake';
import { useAdminConsoleActions, useSaveTask } from '../../admin-console/admin-console-actions';
import { EditDescriptionDialogv2 } from '../EditDescriptionDialogv2';
import { useEndpointFetch } from '../../../../global/network/useEndpointFetch';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { AddCakeDialog } from '../AddCakeDialog';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { ButtonGroupSection } from './ButtonGroupSection';
import { useCampfireTheme } from '../../../../theme/useCampfireTheme';
import { FullscreenDialog } from '../../../../common/dialogs/FullscreenDialog';
import { DeleteCakeDialog } from './DeleteCakeDialog';
import { HiddenCakeDialog } from './HiddenCakeDialog';
import { TooltipMenu } from '../../../../common/tooltip-menu';
import { TaskStage } from '../../admin-console/admin-console-content-pages/admin-console-volunteer-profile/admin-console-volunteer-profile-model';
import { AddTaskStageDialog } from '../../admin-console/admin-console-content-pages/admin-console-volunteer-profile/form-builder/dialogs/AddTaskStageDialog';

export enum TaskType {
  REQUIRED = 'required',
  EXTRA = 'extra',
}

const VOLUNTEER_PROFILE = 'Volunteer Profile';

// Define onboarding option types (matching backend constants)
const ONBOARDING_OPTION_TYPES = {
  ACTIVITY_PREFERENCES: 'activity_preferences',
  AVAILABILITY_SIMPLE: 'availability_simple',
  AVAILABILITY_COMPLEX: 'availability_complex',
  ROLE_APPLICATIONS: 'role_applications',
} as const;

// Helper to create pseudo-tasks from onboarding settings
const createOnboardingPseudoTasks = (
  activityEnrolmentSettings: GetApplicationEnrolActivities | undefined,
  roleApplicationSettings: GetApplicationApplyRoles | undefined,
  availabilitySimpleSettings: GetApplicationAvailabilitySimple | undefined,
  availabilityComplexSettings: GetApplicationAvailabilityComplex | undefined,
  onboardingOptionStages: GetOnboardingOptionStages | undefined
) => {
  const pseudoTasks = [];

  // Helper to find stage assignment for an option type
  const getStageAssignment = (optionType: string) => {
    return (
      onboardingOptionStages?.vm.onboardingOptionStages.find((stage) => stage.optionType === optionType)?.taskStage ||
      null
    );
  };

  // Activity Preferences
  if (activityEnrolmentSettings?.vm.configItem?.configItemValue?.flagValue) {
    const taskStage = getStageAssignment(ONBOARDING_OPTION_TYPES.ACTIVITY_PREFERENCES);
    pseudoTasks.push({
      taskId: `onboarding-${ONBOARDING_OPTION_TYPES.ACTIVITY_PREFERENCES}`,
      optionType: ONBOARDING_OPTION_TYPES.ACTIVITY_PREFERENCES,
      title: 'Activity Preferences',
      description: 'Volunteers indicate their activity preferences during application',
      taskStage,
      isOnboardingOption: true,
    });
  }

  // Role Applications
  if (roleApplicationSettings?.vm.configItem?.configItemValue?.flagValue) {
    const taskStage = getStageAssignment(ONBOARDING_OPTION_TYPES.ROLE_APPLICATIONS);
    pseudoTasks.push({
      taskId: `onboarding-${ONBOARDING_OPTION_TYPES.ROLE_APPLICATIONS}`,
      optionType: ONBOARDING_OPTION_TYPES.ROLE_APPLICATIONS,
      title: 'Role Applications',
      description: 'Volunteers can apply for specific roles during application',
      taskStage,
      isOnboardingOption: true,
    });
  }

  // Simple Availability
  if (availabilitySimpleSettings?.vm.configItem?.configItemValue?.flagValue) {
    const taskStage = getStageAssignment(ONBOARDING_OPTION_TYPES.AVAILABILITY_SIMPLE);
    pseudoTasks.push({
      taskId: `onboarding-${ONBOARDING_OPTION_TYPES.AVAILABILITY_SIMPLE}`,
      optionType: ONBOARDING_OPTION_TYPES.AVAILABILITY_SIMPLE,
      title: 'Simple Availability',
      description: 'Volunteers provide basic availability information',
      taskStage,
      isOnboardingOption: true,
    });
  }

  // Complex Availability
  if (availabilityComplexSettings?.vm.configItem?.configItemValue?.flagValue) {
    const taskStage = getStageAssignment(ONBOARDING_OPTION_TYPES.AVAILABILITY_COMPLEX);
    pseudoTasks.push({
      taskId: `onboarding-${ONBOARDING_OPTION_TYPES.AVAILABILITY_COMPLEX}`,
      optionType: ONBOARDING_OPTION_TYPES.AVAILABILITY_COMPLEX,
      title: 'Complex Availability',
      description: 'Volunteers provide detailed availability schedules',
      taskStage,
      isOnboardingOption: true,
    });
  }

  return pseudoTasks;
};

const TaskHeaderSkeleton = () => {
  return (
    <List disablePadding>
      <Box display='flex' justifyContent='center' width='100%' mt='46px'>
        <Skeleton variant='rect' width='280px' height='40px' style={{ borderRadius: '8px' }} />
      </Box>
      <Box width='100%' mt='36px'>
        <Skeleton variant='rect' width='200px' height='30px' style={{ borderRadius: '4px', marginBottom: '12px' }} />
        <Skeleton variant='rect' width='100%' height='20px' style={{ borderRadius: '4px', marginBottom: '12px' }} />
        <Skeleton variant='rect' width='100%' height='20px' style={{ borderRadius: '4px', marginBottom: '12px' }} />
      </Box>
    </List>
  );
};
const TaskListSkeleton = () => {
  return (
    <List disablePadding style={{ marginTop: '12px' }}>
      {Array.from(Array(3)).map(() => (
        <ListItem disableGutters key={uuidv4()}>
          <Skeleton variant='rect' width='100%' height='130px' style={{ borderRadius: '8px' }} />
        </ListItem>
      ))}
    </List>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: 'flex',
      alignItems: 'stretch',
      flex: '1 1 0',
      minHeight: 0,
      marginLeft: '10px',
    },
    taskContainer: {
      display: 'flex',
      flexDirection: 'column',
      paddingRight: (props: { isMobile: boolean }) => (props.isMobile ? '20px' : '60px'),
      overflowY: 'scroll',
      flex: 1,
    },
    badge: {
      fontSize: '.74em',
      fontWeight: 500,
      padding: 8,
      paddingLeft: 12,
      paddingRight: 12,
      borderRadius: 4,
      backgroundColor: '#D9D9D9',
      color: theme.color.grey.neutral400,
      marginLeft: 16,
    },
    taskList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      marginTop: 25,

      flex: 1,
      paddingBottom: '2rem',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
      marginTop: '2rem',
      justifyContent: 'space-between',
    },
    titleButton: {
      border: '1px solid',
      boxSizing: 'border-box',
      borderRadius: '4px',
      textTransform: 'none',
      padding: '6px 12px',
      minWidth: '45px',
    },
    cakeTitle: {
      fontWeight: 800,
      fontSize: '1.25rem',
      color: theme.color.grey.neutralBrand800,
    },
    cakeDescription: {
      fontWeight: 300,
      color: theme.color.grey.neutral200,
      fontSize: '0.9rem',
      marginTop: '6px',
    },
  })
);

export function TasksList({
  refetchFormCakes,
  onTaskStageRefetchReady,
}: {
  refetchFormCakes: () => void;
  onTaskStageRefetchReady?: (refetchFn: () => Promise<any>) => void;
}) {
  const history = useHistory();
  const location = useLocation();
  const [openAddCakeDialog, setOpenAddCakeDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteCakeDialog, setOpenDeleteCakeDialog] = useState(false);
  const [openHiddenCakeDialog, setOpenHiddenCakeDialog] = useState(false);
  const [cakeId, setCakeId] = useQueryParam('cid', StringParam);
  const [taskId, setTaskId] = useQueryParam('tid', StringParam);
  const [isOrdering, setIsOrdering] = useQueryParam('isOrdering', BooleanParam);
  const [tasks, setTasks] = React.useState<CakeTaskType[]>([]);
  const [taskStageOverrides, setTaskStageOverrides] = React.useState<Record<string, string | null>>({});
  const [editingTaskStage, setEditingTaskStage] = useState<TaskStage | null>(null);
  const [q] = useQueryParam('q', StringParam);
  const [expanded, setExpanded] = useState<boolean>(false);
  const { setSnackbar } = useSnackbar();
  const { data: vmCake, refetch: cakeRefetch, loading: cakeLoading } = useCampfireQuery<TaskListGetCakes, {}>(
    GET_CAKES
  );
  const { data: vmTaskStages, refetch: refetchTaskStages } = useCampfireQuery<GetTaskStages, {}>(GET_TASK_STAGES);

  // Onboarding option stages and settings
  const { data: onboardingOptionStages } = useCampfireQuery<GetOnboardingOptionStages, {}>(
    GET_ONBOARDING_OPTION_STAGES
  );
  const { data: activityEnrolmentSettings } = useCampfireQuery<GetApplicationEnrolActivities, {}>(
    GET_APPLICATION_ENROL_ACTIVITIES
  );
  const { data: roleApplicationSettings } = useCampfireQuery<GetApplicationApplyRoles, {}>(GET_APPLICATION_APPLY_ROLES);
  const { data: availabilitySimpleSettings } = useCampfireQuery<GetApplicationAvailabilitySimple, {}>(
    GET_APPLICATION_AVAILABILITY_SIMPLE
  );
  const { data: availabilityComplexSettings } = useCampfireQuery<GetApplicationAvailabilityComplex, {}>(
    GET_APPLICATION_AVAILABILITY_COMPLEX
  );

  // Pass the refetch function up to parent
  React.useEffect(() => {
    if (onTaskStageRefetchReady && refetchTaskStages) {
      // Pass the actual refetch function, not calling it
      onTaskStageRefetchReady(refetchTaskStages);
    }
  }, [onTaskStageRefetchReady, refetchTaskStages]);

  const [queryCakeDetails, { data: vmCakeDetails, refetch, loading: cakeDetailLoading }] = useCampfireLazyQuery<
    TaskListGetCake,
    TaskListGetCakeVariables
  >(GET_CAKE);

  const saveTask = useSaveTask();
  const cakes = (vmCake?.vm.cakes || ([] as CakeType[])).sort((cA, cB) => cA.order - cB.order);
  const firstCakeId = cakes[0]?.cakeId;
  const selectedCake = cakes.find((cake) => cake.cakeId === cakeId);
  const isCakeIdInList = Boolean(selectedCake);
  const lastCakeOrder = cakes[cakes.length - 1]?.order;
  const selectedCakeDetail = vmCakeDetails?.vm.cake;
  const isHiddenCake = selectedCake?.isHidden;
  const isOnboardingCake = selectedCake?.title === 'Onboarding';
  const removeTask = useEndpointFetch<{ taskId: string }>('/vm/task/remove');
  const removeCake = useEndpointFetch<{ cakeId: string }>('/vm/cake/remove');
  const saveTaskStage = useCampfireFetch({ defer: true });
  const deleteTaskStage = useCampfireFetch({ defer: true });

  const { runSaveCake } = useAdminConsoleActions();
  const apiUrl = useApiUrl();

  React.useEffect(() => {
    if (cakeId) {
      queryCakeDetails({
        variables: {
          cakeId,
        },
      });
    }
  }, [cakeId]);

  useDeepEffect(() => {
    if (!isCakeIdInList || !cakeId) {
      setCakeId(firstCakeId);
    }
  }, [firstCakeId, cakeId, isCakeIdInList]);

  useDeepEffect(() => {
    setTasks(vmCakeDetails?.vm.cake.allTasks || []);
  }, [vmCakeDetails]);

  const onEdit = () => {
    const currentTask = tasks.find((task) => task.taskId === taskId);
    let typeParam = '';

    if (currentTask?.isRefereeCheckTask) {
      typeParam = '&type=referee_check';
    } else if (currentTask?.isEventTask) {
      typeParam = '&type=event';
    } else if ((currentTask as any)?.isPaymentTask || (currentTask as any)?.stripeConfig) {
      typeParam = '&type=stripe_product';
    }

    history.push(`/admin/tasks/task-builder${location.search}${typeParam}`);
  };
  const onDuplicate = () => {
    history.push(`/admin/tasks/task-builder${location.search}&duplicate=1`);
  };
  const onDelete = (deletingTaskId: string) => {
    removeTask
      .run({
        taskId: deletingTaskId,
      })
      .then(() => {
        if (refetch) {
          refetch();
        }
      });
  };

  const onMove = (newTask: any, handleSuccess?: () => void) => {
    saveTask
      .run(newTask)
      .then((res) => {
        if (!res.ok) {
          setSnackbar({
            open: true,
            message: 'Unable to move task',
            variant: 'error',
          });
          return;
        }

        setSnackbar({
          open: true,
          message: 'Task moved successfully',
          variant: 'success',
        });
        setTaskId(undefined);
        if (refetch) {
          refetch();
        }
        if (handleSuccess) handleSuccess();
      })
      .catch(() =>
        setSnackbar({
          open: true,
          message: 'Unable to move task',
          variant: 'error',
        })
      );
  };

  // Legacy onReorder function - replaced by DraggableList implementation
  // const onReorder = (startIdx: any, endIdx: any, reorder: any) => {
  //   setTasks(
  //     profileTasks.concat(
  //       reorder(nonProfileTasks, startIdx, endIdx).map((task: CakeTaskType, taskIndex: number) => {
  //         return {
  //           ...task,
  //           order: taskIndex,
  //         };
  //       })
  //     )
  //   );
  // };

  const onCancel = () => {
    setIsOrdering(false);
    setTasks(vmCakeDetails?.vm.cake.allTasks || []);
  };

  const onSave = () => {
    // Get the current reordered task list and assign them to stages based on their position
    const reorderedTaskStageGroups = groupTasksByStage(nonProfileTasks, taskStageOverrides, onboardingPseudoTasks);
    // Filter out pseudo-tasks when saving - only save real tasks
    const allNonProfileTasks = nonProfileTasks.filter(
      (task) => task.title !== VOLUNTEER_PROFILE && !task.isOnboardingOption
    );

    // Determine which stage each task should belong to based on its current position
    const allTasksWithNewOrder = allNonProfileTasks.map((task, globalIndex) => {
      let cumulativeTaskCount = 0;
      let assignedStageId = null;

      // Find which stage this task should belong to based on its position
      reorderedTaskStageGroups.some((stageGroup) => {
        // Count only real tasks, not pseudo-tasks, for position calculation
        const realTasksCount = stageGroup.tasks.filter((t) => !t.isOnboardingOption).length;
        if (globalIndex < cumulativeTaskCount + realTasksCount) {
          assignedStageId = stageGroup.stageId === 'no-stage' ? null : stageGroup.stageId;
          return true; // break out of the loop
        }
        cumulativeTaskCount += realTasksCount;
        return false; // continue to next iteration
      });

      return {
        ...task,
        programIds: task.programs.map((p) => p.programId),
        roleIds: task.roles.map((r) => r.activityRoleId),
        activityIds: task.activities.map((a) => a.activityId),
        order: globalIndex,
        cakeId,
        taskStageId: assignedStageId,
      };
    });

    Promise.all(allTasksWithNewOrder.map((task) => saveTask.run(task)));
    setSnackbar({
      open: true,
      message: 'Tasks saved',
      variant: 'success',
    });
    setIsOrdering(false);
  };

  const onDeleteCakeClick = () => {
    setOpenDeleteCakeDialog(true);
  };
  const handleDeleteCake = () => {
    if (cakeId) {
      removeCake
        .run({
          cakeId,
        })
        .then((res) => {
          if (!res.ok) {
            setSnackbar({
              open: true,
              message: 'Unable to delete cake',
              variant: 'error',
            });
            return;
          }
          setOpenDeleteCakeDialog(false);
          setSnackbar({
            open: true,
            message: 'Cake removed successfully',
            variant: 'success',
          });
          if (cakeRefetch) {
            cakeRefetch();
          }
          if (refetchFormCakes) {
            refetchFormCakes();
          }
        })
        .catch(() =>
          setSnackbar({
            open: true,
            message: 'Unable to delete cake',
            variant: 'error',
          })
        );
    }
  };

  const handleHiddenCake = () => {
    if (selectedCakeDetail) {
      runSaveCake(
        {
          ...selectedCakeDetail,
          taskIds: selectedCakeDetail.allTasks.filter((task) => task.order !== -1).map((task) => task.taskId),
          isHidden: !selectedCakeDetail.isHidden,
        },

        () => {
          setOpenHiddenCakeDialog(false);
          if (cakeRefetch) {
            cakeRefetch();
          }
          if (refetch) {
            refetch();
          }
          if (refetchFormCakes) {
            refetchFormCakes();
          }
        }
      );
    }
  };

  const handleAddCakeSuccess = () => {
    if (cakeRefetch) {
      cakeRefetch();
    }
    if (refetchFormCakes) {
      refetchFormCakes();
    }
    setOpenAddCakeDialog(false);
  };

  const handleEditTaskStage = async (taskStage: Omit<TaskStage, 'taskStageId'>) => {
    // This function handles both editing existing stages and adding new ones
    // If editingTaskStage exists, we're editing; otherwise we're adding
    const isEditing = !!editingTaskStage;

    try {
      const res = await saveTaskStage.run({
        url: `${apiUrl}/vm/task/stage/save`,
        method: 'post',
        data: {
          ...(isEditing && editingTaskStage && { taskStageId: editingTaskStage.taskStageId }),
          name: taskStage.name,
          automateApproval: taskStage.automateApproval,
          order: taskStage.order,
        },
      });

      if (!res.ok) {
        setSnackbar({
          open: true,
          message: res.data?.message || `Unable to ${isEditing ? 'update' : 'create'} task stage`,
          variant: 'error',
        });
        throw new Error('Failed to save');
      }

      setSnackbar({
        open: true,
        message: `Task stage ${isEditing ? 'updated' : 'created'} successfully`,
        variant: 'success',
      });

      setEditingTaskStage(null);

      // Refresh task stages data
      if (refetchTaskStages) {
        await refetchTaskStages();
      }
      // Also refresh cake details if they contain task stage info
      if (refetch) {
        await refetch();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Unable to ${isEditing ? 'update' : 'create'} task stage`,
        variant: 'error',
      });
      throw error;
    }
  };

  const handleDeleteTaskStage = async () => {
    if (!editingTaskStage) return;

    try {
      const res = await deleteTaskStage.run({
        url: `${apiUrl}/vm/task/stage/delete`,
        method: 'post',
        data: {
          taskStageId: editingTaskStage.taskStageId,
        },
      });

      if (!res.ok) {
        setSnackbar({
          open: true,
          message: res.data?.message || 'Unable to delete task stage',
          variant: 'error',
        });
        throw new Error('Failed to delete');
      }

      setSnackbar({
        open: true,
        message: 'Task stage deleted successfully',
        variant: 'success',
      });

      setEditingTaskStage(null);

      // Refresh task stages data
      if (refetchTaskStages) {
        await refetchTaskStages();
      }
      // Also refresh cake details if they contain task stage info
      if (refetch) {
        await refetch();
      }
    } catch (error) {
      // Error already handled above
    }
  };

  const { isMobile, isMd, theme } = useCampfireTheme();
  const classes = useStyles({ isMobile });
  // Generate pseudo-tasks from onboarding settings
  const onboardingPseudoTasks = React.useMemo(() => {
    if (!isOnboardingCake) return [];

    return createOnboardingPseudoTasks(
      activityEnrolmentSettings,
      roleApplicationSettings,
      availabilitySimpleSettings,
      availabilityComplexSettings,
      onboardingOptionStages
    );
  }, [
    isOnboardingCake,
    activityEnrolmentSettings,
    roleApplicationSettings,
    availabilitySimpleSettings,
    availabilityComplexSettings,
    onboardingOptionStages,
  ]);

  const filteredTasks = tasks.filter(
    (task) => task && task.title && task.title.toLowerCase().includes(q?.toLowerCase() || '')
  );
  const profileTasks = filteredTasks.filter((task) => task.title === VOLUNTEER_PROFILE);
  const nonProfileTasks = filteredTasks
    .filter((task) => task.title !== VOLUNTEER_PROFILE)
    .sort((a, b) => (a.order > b.order ? 1 : -1));

  // Group tasks by stage for display, including empty stages and pseudo-tasks
  const groupTasksByStage = (
    taskList: CakeTaskType[],
    stageOverrides: Record<string, string | null>,
    pseudoTasks: any[] = []
  ) => {
    const allStages = (vmTaskStages?.vm.taskStages || []).sort((a, b) => a.order - b.order);

    // Initialize with all available stages
    const grouped = allStages.reduce((acc, stage) => {
      acc[stage.taskStageId] = {
        stageId: stage.taskStageId,
        stageName: stage.name,
        stageOrder: stage.order,
        tasks: [],
      };
      return acc;
    }, {} as Record<string, { stageId: string; stageName: string | null; stageOrder: number; tasks: CakeTaskType[] }>);

    // Add regular tasks to their respective stages
    taskList.forEach((task) => {
      // Use override if it exists, otherwise use the original task stage
      const overrideStageId = stageOverrides[task.taskId];
      const stageId =
        overrideStageId !== undefined ? overrideStageId || 'no-stage' : task.taskStage?.taskStageId || 'no-stage';

      // If using an override, get the stage info from allStages
      let stageName = null;
      let stageOrder = 999;

      if (stageId !== 'no-stage') {
        const stageInfo = allStages.find((s) => s.taskStageId === stageId);
        if (stageInfo) {
          stageName = stageInfo.name;
          stageOrder = stageInfo.order;
        }
      }

      if (!grouped[stageId]) {
        grouped[stageId] = {
          stageId,
          stageName,
          stageOrder,
          tasks: [],
        };
      }
      grouped[stageId].tasks.push(task);
    });

    // Add pseudo-tasks to their respective stages
    pseudoTasks.forEach((pseudoTask) => {
      const stageId = pseudoTask.taskStage?.taskStageId || 'no-stage';

      let stageName = null;
      let stageOrder = 999;

      if (stageId !== 'no-stage') {
        const stageInfo = allStages.find((s) => s.taskStageId === stageId);
        if (stageInfo) {
          stageName = stageInfo.name;
          stageOrder = stageInfo.order;
        }
      }

      if (!grouped[stageId]) {
        grouped[stageId] = {
          stageId,
          stageName,
          stageOrder,
          tasks: [],
        };
      }
      grouped[stageId].tasks.push(pseudoTask);
    });

    // Sort stages by order, with no-stage first
    return Object.values(grouped).sort((a, b) => {
      if (a.stageId === 'no-stage') return -1;
      if (b.stageId === 'no-stage') return 1;
      return a.stageOrder - b.stageOrder;
    });
  };

  const taskStageGroups = isOnboardingCake
    ? groupTasksByStage(nonProfileTasks, taskStageOverrides, onboardingPseudoTasks)
    : [];

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = React.useState(false);

  return (
    <>
      <div className={classes.container} ref={containerRef}>
        {!isMobile && (
          <div
            id='measure-layer'
            style={{
              display: 'inline-block',
              position: 'absolute',
              visibility: 'hidden',
              zIndex: -1,
              width: isMobile ? '100%' : 'auto',
            }}
          />
        )}
        <Box className={classes.taskContainer}>
          {cakeLoading ? (
            <TaskHeaderSkeleton />
          ) : (
            <Box display='flex' flexDirection='column'>
              <ButtonGroupSection cakes={cakes} expanded={expanded} setOpenAddCakeDialog={setOpenAddCakeDialog} />
              <Box className={classes.titleContainer}>
                <Box>
                  <Box style={{ display: 'flex', flexDirection: 'row' }}>
                    <Typography className={classes.cakeTitle}>{`${selectedCake?.title}`} Tasks</Typography>
                    {selectedCake?.isHidden ? (
                      <Box className={classes.badge} display={'flex'} alignItems={'center'}>
                        <VisibilityOff style={{ fontSize: '15px', paddingRight: '5px' }} /> Hidden
                      </Box>
                    ) : (
                      ''
                    )}
                  </Box>
                  <Box marginTop='1rem'>
                    <Typography className={classes.cakeDescription}>{selectedCake?.description}</Typography>
                  </Box>
                </Box>
                <Box display='flex' style={{ gap: '1rem', alignSelf: 'flex-start' }}>
                  <TabletButton
                    variant={isOrdering ? 'contained' : 'outlined'}
                    color='primary'
                    style={{
                      boxSizing: 'border-box',
                      borderRadius: '4px',
                      textTransform: 'none',
                      padding: !isMobile || !isMd ? '7px 12px' : '7px 7px',
                      minWidth: '40px',
                    }}
                    onClick={() => setIsOrdering(!isOrdering)}
                  >
                    <DragIndicator style={{ marginTop: '1px', fontSize: '16px' }} />
                    <Typography
                      style={{
                        marginLeft: '8px',
                        fontWeight: 500,
                        fontSize: '13px',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      Reorder
                    </Typography>
                  </TabletButton>
                  <TooltipMenu
                    container={containerRef.current} // Beter positioning
                    title={
                      <Box
                        display='flex'
                        flexDirection='column'
                        paddingTop={1}
                        paddingBottom={1}
                        alignItems='flex-start'
                        onClick={() => setMenuOpen(false)}
                      >
                        <TabletButton
                          variant='text'
                          style={{
                            boxSizing: 'border-box',
                            textTransform: 'none',
                            padding: !isMobile || !isMd ? '7px 12px' : '7px 7px',
                            minWidth: '40px',
                            color: theme.alert.blue.extraLight,
                          }}
                          onClick={() => setOpenEditDialog(true)}
                        >
                          <BorderColor style={{ marginTop: '1px', fontSize: '16px' }} />
                          <Typography
                            style={{
                              marginLeft: '10px',
                              fontWeight: 500,
                              fontSize: '14px',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              display: isMobile || isMd ? 'none' : 'inherit',
                            }}
                          >
                            Edit
                          </Typography>
                        </TabletButton>

                        {selectedCake?.title !== 'Onboarding' ? (
                          <TabletButton
                            variant='text'
                            style={{
                              boxSizing: 'border-box',
                              textTransform: 'none',
                              padding: !isMobile || !isMd ? '7px 12px' : '7px 7px',
                              minWidth: '40px',
                              color: theme.alert.blue.extraLight,
                            }}
                            onClick={() => setOpenHiddenCakeDialog(true)}
                          >
                            {isHiddenCake ? (
                              <Visibility style={{ marginTop: '1px', fontSize: '16px' }} />
                            ) : (
                              <VisibilityOff style={{ marginTop: '1px', fontSize: '16px' }} />
                            )}
                            <Typography
                              style={{
                                marginLeft: '10px',
                                fontWeight: 500,
                                fontSize: '14px',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                display: isMobile || isMd ? 'none' : 'inherit',
                              }}
                            >
                              {`${isHiddenCake ? 'Show' : 'Hide'}`}
                            </Typography>
                          </TabletButton>
                        ) : (
                          ''
                        )}
                        {selectedCake?.title !== 'Onboarding' && (
                          <TabletButton
                            variant='text'
                            style={{
                              boxSizing: 'border-box',
                              textTransform: 'none',
                              padding: !isMobile || !isMd ? '7px 12px' : '7px 7px',
                              minWidth: '40px',
                              color: theme.alert.red.light,
                            }}
                            onClick={onDeleteCakeClick}
                            disabled={cakeDetailLoading}
                          >
                            <DeleteOutlined style={{ marginTop: '1px', fontSize: '16px' }} />
                            <Typography
                              style={{
                                marginLeft: '10px',
                                fontWeight: 500,
                                fontSize: '14px',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                display: isMobile || isMd ? 'none' : 'inherit',
                              }}
                            >
                              Delete
                            </Typography>
                          </TabletButton>
                        )}
                      </Box>
                    }
                    open={menuOpen}
                    onClose={() => setMenuOpen(false)}
                  >
                    <TabletButton
                      size='small'
                      variant='outlined'
                      color='primary'
                      aria-controls='options-menu'
                      aria-haspopup='true'
                      onClick={() => setMenuOpen(true)}
                      endIcon={<ExpandMore />}
                      style={{
                        height: '100%',
                        border: '1px solid #9e9e9e',
                        boxSizing: 'border-box',
                        borderRadius: '4px',
                        padding: '2px 7px',
                        marginRight: '10px',
                        color: theme.color.grey.neutral300,
                      }}
                    >
                      Options
                    </TabletButton>
                  </TooltipMenu>
                </Box>
              </Box>
            </Box>
          )}
          {cakeDetailLoading ? (
            <TaskListSkeleton />
          ) : (
            <Box className={classes.taskList}>
              <Box>
                {profileTasks.map((task) => (
                  <Task key={task.taskId} task={task} onClick={() => setTaskId(task.taskId)} />
                ))}
              </Box>
              {isOrdering ? (
                isOnboardingCake ? (
                  <>
                    <DragDropContext
                      onDragEnd={(result: any) => {
                        // Handle drag and drop between stages
                        if (!result.destination) return;

                        const sourceStageId = result.source.droppableId;
                        const destStageId = result.destination.droppableId;
                        const sourceIndex = result.source.index;

                        // Make a copy of taskStageGroups
                        const newGroups = [...taskStageGroups];
                        const sourceGroup = newGroups.find((g) => g.stageId === sourceStageId);
                        const destGroup = newGroups.find((g) => g.stageId === destStageId);

                        if (!sourceGroup || !destGroup) return;

                        // Get the task being moved
                        const movedTask = sourceGroup.tasks[sourceIndex];

                        // Update the stage override for this task
                        setTaskStageOverrides((prev) => ({
                          ...prev,
                          [movedTask.taskId]: destStageId === 'no-stage' ? null : destStageId,
                        }));
                      }}
                    >
                      <Box style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        {taskStageGroups.map((stageGroup) => (
                          <Box key={stageGroup.stageId}>
                            {stageGroup.stageName && (
                              <Box
                                style={{
                                  border: '1px solid #E0E0E0',
                                  borderRadius: '8px',
                                  padding: '16px',
                                  backgroundColor: 'transparent',
                                  position: 'relative',
                                  minHeight: '100px',
                                }}
                              >
                                <Typography
                                  style={{
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: theme.color.grey.neutral400,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    position: 'absolute',
                                    top: '-8px',
                                    left: '12px',
                                    backgroundColor: 'white',
                                    padding: '0 8px',
                                  }}
                                >
                                  Stage {stageGroup.stageOrder}: {stageGroup.stageName}
                                </Typography>
                                <TabletButton
                                  size='small'
                                  variant='text'
                                  style={{
                                    position: 'absolute',
                                    top: '-8px',
                                    right: '12px',
                                    backgroundColor: 'white',
                                    padding: '2px 6px',
                                    minWidth: 'auto',
                                    fontSize: '10px',
                                    color: theme.color.grey.neutral400,
                                  }}
                                  onClick={() => {
                                    // Find the stage data and set it for editing
                                    const stageData = vmTaskStages?.vm.taskStages.find(
                                      (s) => s.taskStageId === stageGroup.stageId
                                    );
                                    if (stageData) {
                                      setEditingTaskStage({
                                        taskStageId: stageData.taskStageId,
                                        name: stageData.name,
                                        automateApproval: stageData.automateApproval,
                                        order: stageData.order,
                                      });
                                    }
                                  }}
                                >
                                  Edit
                                </TabletButton>

                                <Droppable droppableId={stageGroup.stageId}>
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.droppableProps}
                                      style={{
                                        paddingTop: '16px',
                                        minHeight: '80px',
                                        backgroundColor: snapshot.isDraggingOver
                                          ? 'rgba(25, 118, 210, 0.08)'
                                          : 'transparent',
                                        borderRadius: '4px',
                                        transition: 'background-color 0.2s ease',
                                        padding: '16px 8px',
                                      }}
                                    >
                                      {stageGroup.tasks.length === 0 ? (
                                        <Box
                                          style={{
                                            padding: '30px',
                                            textAlign: 'center',
                                            border: '2px dashed #E0E0E0',
                                            borderRadius: '4px',
                                            color: theme.color.grey.neutral200,
                                            fontStyle: 'italic',
                                            fontSize: '14px',
                                            backgroundColor: snapshot.isDraggingOver ? 'white' : 'transparent',
                                          }}
                                        >
                                          Drop tasks here
                                        </Box>
                                      ) : (
                                        <Box style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                          {stageGroup.tasks.map((task, index) =>
                                            task.isOnboardingOption ? (
                                              // Pseudo-tasks are not draggable
                                              <OnboardingOptionTask key={task.taskId} title={task.title} />
                                            ) : (
                                              <Draggable key={task.taskId} draggableId={task.taskId} index={index}>
                                                {(dragProvided, dragSnapshot) => (
                                                  <div
                                                    ref={dragProvided.innerRef}
                                                    {...dragProvided.draggableProps}
                                                    style={{
                                                      ...dragProvided.draggableProps.style,
                                                      opacity: dragSnapshot.isDragging ? 0.8 : 1,
                                                    }}
                                                  >
                                                    <Box display='flex' flex='1 1 auto' position='relative'>
                                                      <Box
                                                        marginRight='2rem'
                                                        marginLeft='1rem'
                                                        {...dragProvided.dragHandleProps}
                                                      >
                                                        <DraggableListItemHandle />
                                                      </Box>
                                                      <Box
                                                        display='flex'
                                                        flex='1 1 auto'
                                                        alignContent='center'
                                                        alignItems='center'
                                                      >
                                                        <Task task={task} onClick={() => setTaskId(task.taskId)} />
                                                      </Box>
                                                    </Box>
                                                  </div>
                                                )}
                                              </Draggable>
                                            )
                                          )}
                                        </Box>
                                      )}
                                      {provided.placeholder}
                                    </div>
                                  )}
                                </Droppable>
                              </Box>
                            )}

                            {/* No-stage tasks */}
                            {!stageGroup.stageName && stageGroup.tasks.length > 0 && (
                              <Droppable droppableId={stageGroup.stageId}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                    style={{
                                      minHeight: '60px',
                                      backgroundColor: snapshot.isDraggingOver
                                        ? 'rgba(25, 118, 210, 0.08)'
                                        : 'transparent',
                                      borderRadius: '4px',
                                      padding: snapshot.isDraggingOver ? '8px' : '0',
                                      transition: 'all 0.2s ease',
                                    }}
                                  >
                                    <Box style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                      {stageGroup.tasks.map((task, index) =>
                                        task.isOnboardingOption ? (
                                          // Pseudo-tasks are not draggable
                                          <OnboardingOptionTask key={task.taskId} title={task.title} />
                                        ) : (
                                          <Draggable key={task.taskId} draggableId={task.taskId} index={index}>
                                            {(taskProvided, taskSnapshot) => (
                                              <div
                                                ref={taskProvided.innerRef}
                                                {...taskProvided.draggableProps}
                                                style={{
                                                  ...taskProvided.draggableProps.style,
                                                  opacity: taskSnapshot.isDragging ? 0.8 : 1,
                                                }}
                                              >
                                                <Box display='flex' flex='1 1 auto' position='relative'>
                                                  <Box
                                                    marginRight='2rem'
                                                    marginLeft='1rem'
                                                    {...taskProvided.dragHandleProps}
                                                  >
                                                    <DraggableListItemHandle />
                                                  </Box>
                                                  <Box
                                                    display='flex'
                                                    flex='1 1 auto'
                                                    alignContent='center'
                                                    alignItems='center'
                                                  >
                                                    <Task task={task} onClick={() => setTaskId(task.taskId)} />
                                                  </Box>
                                                </Box>
                                              </div>
                                            )}
                                          </Draggable>
                                        )
                                      )}
                                    </Box>
                                    {provided.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </DragDropContext>
                    <Box display='flex' justifyContent='flex-end' style={{ marginTop: '20px' }}>
                      <Box display='flex' style={{ gap: '1rem' }}>
                        <Button
                          variant={'text'}
                          style={{
                            boxSizing: 'border-box',
                            borderRadius: '6px',
                            textTransform: 'none',
                            padding: !isMobile || !isMd ? '14px 22px' : '14px 14px',
                            minWidth: '40px',
                          }}
                          onClick={onCancel}
                        >
                          <Typography style={{ fontSize: '13px', fontWeight: 600 }}>Cancel</Typography>
                        </Button>
                        <TabletButton
                          variant='contained'
                          style={{
                            boxSizing: 'border-box',
                            borderRadius: '6px',
                            textTransform: 'none',
                            padding: !isMobile || !isMd ? '14px 22px' : '14px 14px',
                            minWidth: '40px',
                          }}
                          color='primary'
                          onClick={onSave}
                        >
                          <Typography style={{ fontSize: '13px', fontWeight: 600 }}>Save</Typography>
                        </TabletButton>
                      </Box>
                    </Box>
                  </>
                ) : (
                  <>
                    <DragDropContext
                      onDragEnd={(result: any) => {
                        // Handle simple drag and drop for non-onboarding cakes
                        if (!result.destination) return;

                        // For non-onboarding cakes, just reorder the tasks
                        const reorderedTasks = Array.from(nonProfileTasks);
                        const [removed] = reorderedTasks.splice(result.source.index, 1);
                        reorderedTasks.splice(result.destination.index, 0, removed);

                        // Update task orders
                        const updatedTasks = reorderedTasks.map((task, index) => ({
                          ...task,
                          order: index,
                        }));

                        setTasks([...profileTasks, ...updatedTasks]);
                      }}
                    >
                      <Droppable droppableId='non-onboarding-tasks'>
                        {(provided) => (
                          <Box className={classes.taskList} ref={provided.innerRef} {...provided.droppableProps}>
                            {nonProfileTasks.map((task, index) => (
                              <DraggableTask
                                key={task.taskId}
                                task={task}
                                index={index}
                                onClick={() => setTaskId(task.taskId)}
                              />
                            ))}
                            {provided.placeholder}
                          </Box>
                        )}
                      </Droppable>
                    </DragDropContext>
                    <Box display='flex' justifyContent='flex-end' style={{ marginTop: '20px' }}>
                      <Box display='flex' style={{ gap: '1rem' }}>
                        <Button
                          variant={'text'}
                          style={{
                            boxSizing: 'border-box',
                            borderRadius: '6px',
                            textTransform: 'none',
                            padding: !isMobile || !isMd ? '14px 22px' : '14px 14px',
                            minWidth: '40px',
                          }}
                          onClick={onCancel}
                        >
                          <Typography style={{ fontSize: '13px', fontWeight: 600 }}>Cancel</Typography>
                        </Button>
                        <TabletButton
                          variant='contained'
                          style={{
                            boxSizing: 'border-box',
                            borderRadius: '6px',
                            textTransform: 'none',
                            padding: !isMobile || !isMd ? '14px 22px' : '14px 14px',
                            minWidth: '40px',
                          }}
                          color='primary'
                          onClick={onSave}
                        >
                          <Typography style={{ fontSize: '13px', fontWeight: 600 }}>Save</Typography>
                        </TabletButton>
                      </Box>
                    </Box>
                  </>
                )
              ) : (
                <Box style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  {isOnboardingCake
                    ? taskStageGroups.map((stageGroup) => (
                        <Box
                          key={stageGroup.stageId}
                          style={{
                            border: stageGroup.stageName ? '1px solid #E0E0E0' : 'none',
                            borderRadius: '8px',
                            padding: stageGroup.stageName ? '16px' : '0',
                            marginBottom: '20px',
                            backgroundColor: 'transparent',
                            position: 'relative',
                          }}
                        >
                          {stageGroup.stageName && (
                            <>
                              <Typography
                                style={{
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  color: theme.color.grey.neutral400,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                  position: 'absolute',
                                  top: '-8px',
                                  left: '12px',
                                  backgroundColor: 'white',
                                  padding: '0 8px',
                                }}
                              >
                                Stage {stageGroup.stageOrder}: {stageGroup.stageName}
                              </Typography>
                              <TabletButton
                                size='small'
                                variant='text'
                                style={{
                                  position: 'absolute',
                                  top: '-8px',
                                  right: '12px',
                                  backgroundColor: 'white',
                                  padding: '2px 6px',
                                  minWidth: 'auto',
                                  fontSize: '10px',
                                  color: theme.color.grey.neutral400,
                                }}
                                onClick={() => {
                                  // Find the stage data and set it for editing
                                  const stageData = vmTaskStages?.vm.taskStages.find(
                                    (s) => s.taskStageId === stageGroup.stageId
                                  );
                                  if (stageData) {
                                    setEditingTaskStage({
                                      taskStageId: stageData.taskStageId,
                                      name: stageData.name,
                                      automateApproval: stageData.automateApproval,
                                      order: stageData.order,
                                    });
                                  }
                                }}
                              >
                                Edit
                              </TabletButton>
                            </>
                          )}
                          <Box style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            {stageGroup.tasks.length === 0 && stageGroup.stageName ? (
                              <Typography
                                style={{
                                  fontSize: '14px',
                                  color: theme.color.grey.neutral200,
                                  fontStyle: 'italic',
                                  textAlign: 'center',
                                  padding: '20px 0',
                                }}
                              >
                                No tasks in this stage
                              </Typography>
                            ) : (
                              stageGroup.tasks.map((task) =>
                                task.isOnboardingOption ? (
                                  <OnboardingOptionTask key={task.taskId} title={task.title} />
                                ) : (
                                  <Task key={task.taskId} task={task} onClick={() => setTaskId(task.taskId)} />
                                )
                              )
                            )}
                          </Box>
                        </Box>
                      ))
                    : // Non-onboarding cakes: just show tasks without stages
                      nonProfileTasks.map((task) => (
                        <Task key={task.taskId} task={task} onClick={() => setTaskId(task.taskId)} />
                      ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
        {isMobile ? (
          <FullscreenDialog
            fullWidth
            title=''
            fullScreen
            maxWidth={'xs'}
            scroll='paper'
            open={!!taskId}
            close={() => setTaskId(undefined)}
          >
            <Box>
              {taskId ? (
                <TaskDetails
                  taskId={taskId}
                  onDelete={() => onDelete(taskId)}
                  onMove={onMove}
                  onEdit={onEdit}
                  onDuplicate={onDuplicate}
                  cakes={cakes}
                />
              ) : null}
            </Box>
          </FullscreenDialog>
        ) : taskId ? (
          <CollapsibleSideBar setSiblingExpanded={setExpanded}>
            <Box>
              <TaskDetails
                taskId={taskId}
                onDelete={() => onDelete(taskId)}
                onMove={onMove}
                onEdit={onEdit}
                onDuplicate={onDuplicate}
                onClose={() => setTaskId(undefined)}
                cakes={cakes}
              />
            </Box>
          </CollapsibleSideBar>
        ) : null}
      </div>
      {vmCakeDetails?.vm.cake ? (
        <EditDescriptionDialogv2
          open={openEditDialog}
          close={() => setOpenEditDialog(false)}
          cake={vmCakeDetails.vm.cake}
          refetch={cakeRefetch}
        />
      ) : null}
      {openAddCakeDialog ? (
        <AddCakeDialog
          open={openAddCakeDialog}
          order={lastCakeOrder ? lastCakeOrder + 1 : 0}
          close={() => setOpenAddCakeDialog(false)}
          handleSuccess={handleAddCakeSuccess}
        />
      ) : null}

      {openDeleteCakeDialog ? (
        <DeleteCakeDialog
          open={openDeleteCakeDialog}
          hasTasks={tasks.length > 0}
          cakeName={selectedCake?.title}
          onClose={() => setOpenDeleteCakeDialog(false)}
          onConfirm={handleDeleteCake}
        />
      ) : null}

      {openHiddenCakeDialog && selectedCake ? (
        <HiddenCakeDialog
          open={openHiddenCakeDialog}
          isHidden={selectedCake.isHidden}
          cakeName={selectedCake.title}
          onClose={() => setOpenHiddenCakeDialog(false)}
          onConfirm={handleHiddenCake}
        />
      ) : null}

      <AddTaskStageDialog
        open={!!editingTaskStage}
        onClose={() => setEditingTaskStage(null)}
        onSubmit={handleEditTaskStage}
        onDelete={handleDeleteTaskStage}
        editingStage={editingTaskStage}
        existingStages={vmTaskStages?.vm.taskStages || []}
        isLoading={saveTaskStage.isLoading || deleteTaskStage.isLoading}
        hasTasksInStage={
          editingTaskStage
            ? (taskStageGroups.find((g) => g.stageId === editingTaskStage.taskStageId)?.tasks.length ?? 0) > 0
            : false
        }
      />
    </>
  );
}
